import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EMPTY_FILTERS, isFiltered, readFilters, toApiQuery, writeFilters, type ListApiQuery, type ListFilterState } from './listFilters';

/** List filters kept in the address bar. `onChange` runs after every change (pages use it to go back to the first page). */
export function useListFilters(onChange?: () => void): {
  readonly filters: ListFilterState;
  readonly query: ListApiQuery;
  readonly key: string;
  readonly filtered: boolean;
  readonly set: (patch: Partial<ListFilterState>) => void;
  readonly reset: () => void;
} {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const query = useMemo(() => toApiQuery(filters), [filters]);
  const key = useMemo(() => JSON.stringify(query), [query]);
  const set = useCallback(
    (patch: Partial<ListFilterState>) => {
      setParams(writeFilters({ ...filters, ...patch }), { replace: true });
      onChange?.();
    },
    [filters, setParams, onChange],
  );
  const reset = useCallback(() => {
    setParams(writeFilters(EMPTY_FILTERS), { replace: true });
    onChange?.();
  }, [setParams, onChange]);
  return { filters, query, key, filtered: isFiltered(filters), set, reset };
}
